import type { StagedDocument } from '@/components/design-system/upload/ds-upload.types';
import { getAddedSetElements, getRemovedSetElements } from '@/shared/utils/set-difference';
import { useFileDocumentStore } from '@/stores/file-document.store';
import type { TemplateRef } from 'vue';
import { ref, watch } from 'vue';

export const useDocumentUploadForm = ({ esignPreparingDialog }: { esignPreparingDialog: TemplateRef<HTMLDialogElement> }) => {
    const stagedFiles = ref<StagedDocument[]>([]);
    const sendNotification = ref(true);
    const uploadError = ref<string | boolean>();
    const fileDocumentStore = useFileDocumentStore();

    const resetForm = () => {
        stagedFiles.value = [];
        sendNotification.value = true;
        fileDocumentStore.documentActionClients = [];
        fileDocumentStore.selectedDocumentTemplates = [];
        fileDocumentStore.clearAllDocuments();
    };

    /** Apply changes from the template picker to the list of document configs  */
    watch(
        () => fileDocumentStore.selectedDocumentTemplates,
        (newSelectedOptions, oldSelectedOptions) => {
            const selectedIds = new Set(newSelectedOptions.map(template => template.id));
            const oldSelectedIds = new Set(oldSelectedOptions.map(template => template.id));

            const added = getAddedSetElements(oldSelectedIds, selectedIds);

            added.forEach(documentId => {
                const template = newSelectedOptions.find(t => t.id === documentId);
                if (template) {
                    fileDocumentStore.addDocument({
                        documentId: template.id,
                        template,
                    });
                }
            });

            const removed = getRemovedSetElements(oldSelectedIds, selectedIds);
            removed.forEach(documentId => {
                fileDocumentStore.removeDocument(documentId);
            });
        },
    );

    /** Watch for changes on the manually uploaded files and creates document configs in the store */
    watch(
        stagedFiles,
        newFiles => {
            newFiles.forEach(stagedDocument => {
                fileDocumentStore.addDocument({ documentId: stagedDocument.id, file: stagedDocument.file });
            });
        },
        { deep: true },
    );

    /** Clean up staged files that have been removed from the store */
    watch(
        fileDocumentStore.documentConfigs,
        () => {
            stagedFiles.value = stagedFiles.value.filter(stagedDoc => fileDocumentStore.documentConfigs.has(stagedDoc.id));
        },
        { deep: true },
    );

    /** Handles UI-blocking dialog for e-sign edit procedure (invoked in a different tab) */
    watch(
        () => fileDocumentStore.isPreparingESign,
        isPreparingEsign => {
            if (isPreparingEsign) {
                // show modal ensures the loading dialog is rendered on the top layer so it
                // can cover DsSheet
                esignPreparingDialog.value?.showModal();
            } else {
                esignPreparingDialog.value?.close();
            }
        },
    );

    return {
        stagedFiles,
        sendNotification,
        uploadError,
        resetForm,
    };
};
